import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { isCancellationOrNonRenewalDocument, isRecognizedMemberDocument } from '@ourbranch/document-utils';

import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import Checkbox from 'core/components/checkbox';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { addressFormatter } from 'core/helpers/formatters';
import emailIcon from 'core/assets/svg/email.svg';
import useStyles from './send-document.styles';

/**
 * Staff "Send to member" action on the policy Documents tab (#36066): re-send an EXISTING policy
 * document to the member by Email and/or a physical Lob letter, at the member's CURRENT account
 * mailing address — the same address the cancellation-confirm path uses. The modal shows that address
 * so staff can confirm it before sending.
 *
 * Channel eligibility is tied to the document itself: only cancellation / non-renewal notices can be
 * mailed (the only documents the Lob path supports), and only recognized member-facing documents can
 * be emailed, so an internal or unknown document is never put in a member's hands. A channel whose
 * target is missing (no email / no mailing address on file) is disabled with an explanatory note.
 */
const SendDocument = observer(function SendDocument({ d }) {
  const classes = useStyles();
  const toast = useToast();
  const {
    account: {
      email,
      mailingAddress,
      policies: {
        policy: { policy },
        sendPolicyDocument
      }
    }
  } = useStore();

  // The full policy id for this document row (path = <prefix>/<accountId>/<FULL-POLICY-ID>/<file>).
  const policyId = d.path.split('/')[3] || policy?.id;

  const hasEmail = !!email?.trim();
  const hasMailingAddress = !!mailingAddress?.address?.trim();
  const mailable = isCancellationOrNonRenewalDocument(d.path);
  const emailable = isRecognizedMemberDocument(d.path);

  const canEmail = hasEmail && emailable;
  const canLetter = hasMailingAddress && mailable;

  let emailLabel = 'Email';
  if (!emailable) {
    emailLabel = 'Email — this document type can’t be emailed';
  } else if (!hasEmail) {
    emailLabel = 'Email — no email address on file';
  }

  let letterLabel = 'Letter (mailed)';
  if (!mailable) {
    letterLabel = 'Letter — only cancellation/non-renewal notices can be mailed';
  } else if (!hasMailingAddress) {
    letterLabel = 'Letter — no mailing address on file';
  }

  const [open, setOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendLetter, setSendLetter] = useState(false);
  const [sending, setSending] = useState(false);

  const openModal = () => {
    // Default to email when possible (the least-surprising, cheapest channel); staff add the letter.
    setSendEmail(canEmail);
    setSendLetter(!canEmail && canLetter);
    setOpen(true);
  };

  const canSend = (sendEmail && canEmail) || (sendLetter && canLetter);

  const handleSend = async () => {
    if (!canSend) {
      return;
    }
    setSending(true);
    try {
      const res = await sendPolicyDocument({ policyId, docPath: d.path, sendEmail, sendLetter });
      if (res?.success) {
        toast.notify({
          type: 'success',
          message: 'Document send requested. It can take a few minutes to be delivered.'
        });
        setOpen(false);
      } else {
        toast.notify({ type: 'error', message: res?.reason || 'Could not send the document.' });
      }
    } catch (error) {
      toast.notify({ type: 'error', message: `An error occurred. ${error.message.replace(':', '').trim()}` });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <IconButtonWithTooltip
        size="small"
        tooltip="Send to member"
        icon={emailIcon}
        onClick={(e) => {
          // Don't let the click fall through to the row, which downloads the document.
          e.preventDefault();
          e.stopPropagation();
          openModal();
        }}
      />
      <BaseDialog
        open={open}
        onClose={() => setOpen(false)}
        className={classes.dialog}
        // The dialog renders in a portal, but React still bubbles its click events up the component
        // tree to the enclosing SelectableRow (onClick={downloadDocument}) — so without this, ticking
        // a checkbox, clicking Send, or the backdrop/close would also open the PDF in a new tab. Stop
        // propagation at the dialog root so no in-modal click reaches the row.
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.content}>
          <Label type="h1" className={classes.title}>
            Send to member
          </Label>
          <Label type="body3" className={classes.subtitle}>
            Re-send “{d.friendlyName}” to the member. It is only sent to the insured.
          </Label>

          <div className={classes.options}>
            <Checkbox
              id="send-document-email"
              mode="light"
              label={emailLabel}
              value={sendEmail}
              disabled={!canEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <Checkbox
              id="send-document-letter"
              mode="light"
              label={letterLabel}
              value={sendLetter}
              disabled={!canLetter}
              onChange={(e) => setSendLetter(e.target.checked)}
            />
          </div>

          {hasMailingAddress && (
            <Label type="infoLabel" className={classes.addressLine}>
              Letters are mailed to the member’s current address on file: {addressFormatter(mailingAddress)}
            </Label>
          )}

          {!canEmail && !canLetter && (
            <NotificationCard className={classes.notification} type="light">
              <Label type="body3">
                This document can’t be sent to the member. It must be a cancellation/non-renewal notice to mail, or a
                recognized member document to email, and the member must have the matching contact details on file.
              </Label>
            </NotificationCard>
          )}

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSend}
              disabled={!canSend || sending}
              loading={sending}
            >
              Send
            </Button>
          </div>
        </div>
      </BaseDialog>
    </>
  );
});

export default SendDocument;
